import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
    Calendar, Layers, PlusCircle, X, CheckCircle2, AlertTriangle, Trash2, Edit3, ShieldCheck, ChevronDown, ChevronRight 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import { getUserPermissions } from '../../utils/permissions_utils';
import API, { socket } from '../../services/api';

const DecoupagePeriode = ({ user }) => {
    const navigate = useNavigate();
    const userPerms = useMemo(() => getUserPermissions(), []);
    
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [confirmAction, setConfirmAction] = useState({ show: false, id: null, type: null, message: '' });
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null); // ID du découpage en cours de modification
    
    // Données métiers
    const [decoupages, setDecoupages] = useState([]);
    const [annees, setAnnees] = useState([]);
    
    // Filtre par année scolaire
    const [selectedAnneeId, setSelectedAnneeId] = useState('');
    
    // Accordéon exclusif (un seul découpage ouvert à la fois)
    const [decoupageOuvertId, setDecoupageOuvertId] = useState(null);
    const [periodesParDecoupage, setPeriodesParDecoupage] = useState({});

    // Formulaire et état d'édition pour les périodes dans l'accordéon
    const [editingPeriodeId, setEditingPeriodeId] = useState(null);
    const [tempPeriodeForm, setTempPeriodeForm] = useState({
        libelle: '',
        ordre: 1,
        date_debut: '',
        date_fin: '',
        statut: 'OUVERTE'
    });

    // Formulaire principal pour les types de découpage (type_periodicite non pré-rempli mais requis)
    const [decoupageForm, setDecoupageForm] = useState({
        annee_scolaire_id: '',
        libelle: '',
        type_periodicite: ''
    });

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 5000);
    };

    // --- CHARGEMENT DES DONNÉES ---
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const resAnnees = await API.get('/scolarite/annees/liste');
            let anneeIdActive = selectedAnneeId;
            
            if (resAnnees.data.success) {
                const listeAnnees = resAnnees.data.data;
                setAnnees(listeAnnees);
                const ouverte = listeAnnees.find(a => a.statut === 'OUVERT') || listeAnnees[0];
                if (ouverte && !selectedAnneeId) {
                    anneeIdActive = ouverte.id;
                    setSelectedAnneeId(ouverte.id);
                }
            }

            const url = anneeIdActive ? `/scolarite/decoupages/liste?annee_scolaire_id=${anneeIdActive}` : '/scolarite/decoupages/liste';
            const resDec = await API.get(url);
            if (resDec.data.success) {
                const decList = resDec.data.data;
                setDecoupages(decList);

                const regroupement = {};
                for (const dec of decList) {
                    try {
                        const resPer = await API.get(`/scolarite/periodes/liste?decoupage_id=${dec.id}`);
                        regroupement[dec.id] = resPer.data.success ? resPer.data.data : [];
                    } catch (e) {
                        regroupement[dec.id] = [];
                    }
                }
                setPeriodesParDecoupage(regroupement);
            }
        } catch (err) {
            showToast("Erreur lors du chargement des données", "error");
        } finally {
            setLoading(false);
        }
    }, [selectedAnneeId]);

    useEffect(() => {
        fetchData();
        if (socket && user?.company_id) {
            const room = String(user.company_id);
            socket.emit('join_company', room);
            socket.on('DATA_EVENT', (event) => {
                if (['types_decoupage', 'periodes_scolaires', 'annees_scolaires'].includes(event.table)) {
                    fetchData();
                }
            });
        }
        return () => {
            if (socket) socket.off('DATA_EVENT');
        };
    }, [fetchData, user?.company_id]);

    const resetForms = () => {
        setShowForm(false);
        setEditingId(null);
        setDecoupageForm({ annee_scolaire_id: selectedAnneeId || '', libelle: '', type_periodicite: '' });
    };

    // --- ACCORDÉON EXCLUSIF ---
    const toggleAccordion = (decoupageId) => {
        if (decoupageOuvertId === decoupageId) {
            setDecoupageOuvertId(null);
            setEditingPeriodeId(null);
        } else {
            setDecoupageOuvertId(decoupageId);
            setEditingPeriodeId(null);
            const existantes = periodesParDecoupage[decoupageId] || [];
            let defaultStartDate = '';
            if (existantes.length > 0) {
                const lastPeriode = existantes[existantes.length - 1];
                if (lastPeriode.date_fin) {
                    const nextDate = new Date(lastPeriode.date_fin);
                    nextDate.setDate(nextDate.getDate() + 1);
                    defaultStartDate = nextDate.toISOString().split('T')[0];
                }
            }
            setTempPeriodeForm({
                libelle: '',
                ordre: existantes.length + 1,
                date_debut: defaultStartDate,
                date_fin: '',
                statut: 'OUVERTE'
            });
        }
    };

    const handleTempDateDebutChange = (e) => {
        const val = e.target.value;
        let finVal = tempPeriodeForm.date_fin;

        if (val) {
            const d = new Date(val);
            d.setDate(d.getDate() + 1);
            const nextDayStr = d.toISOString().split('T')[0];
            if (!finVal || finVal <= val) {
                finVal = nextDayStr;
            }
        }

        setTempPeriodeForm(prev => ({
            ...prev,
            date_debut: val,
            date_fin: finVal
        }));
    };

    const getMinDateFin = (dateDebut) => {
        if (!dateDebut) return undefined;
        const d = new Date(dateDebut);
        d.setDate(d.getDate() + 1);
        return d.toISOString().split('T')[0];
    };

    // --- AJOUT OU MODIFICATION D'UNE PÉRIODE ---
    const handleSavePeriode = async (decoupage) => {
        if (!tempPeriodeForm.libelle || !tempPeriodeForm.date_debut || !tempPeriodeForm.date_fin) {
            return showToast("Veuillez remplir tous les champs de la période", "error");
        }

        const debut = new Date(tempPeriodeForm.date_debut);
        const fin = new Date(tempPeriodeForm.date_fin);

        if (debut >= fin) {
            return showToast("La date de fin doit être strictement supérieure à la date de début.", "error");
        }

        const periodesDec = periodesParDecoupage[decoupage.id] || [];
        for (const p of periodesDec) {
            if (editingPeriodeId && p.id === editingPeriodeId) continue;
            const lDebut = new Date(p.date_debut);
            const lFin = new Date(p.date_fin);
            if (debut <= lFin && fin >= lDebut) {
                return showToast(`Erreur : Cette période chevauche la période existante "${p.libelle}" (${p.date_debut?.split('T')[0]} au ${p.date_fin?.split('T')[0]}).`, "error");
            }
        }

        setActionLoading(true);
        try {
            const payload = {
                annee_scolaire_id: decoupage.annee_scolaire_id,
                decoupage_id: decoupage.id,
                libelle: tempPeriodeForm.libelle.toUpperCase(),
                ordre: parseInt(tempPeriodeForm.ordre) || (periodesDec.length + 1),
                date_debut: tempPeriodeForm.date_debut,
                date_fin: tempPeriodeForm.date_fin,
                statut: tempPeriodeForm.statut
            };

            if (editingPeriodeId) {
                await API.put(`/scolarite/periodes/${editingPeriodeId}`, payload);
                showToast("Période mise à jour avec succès.");
            } else {
                await API.post('/scolarite/periodes/creer', payload);
                showToast("Période créée avec succès.");
            }

            setEditingPeriodeId(null);
            const nextDate = new Date(fin);
            nextDate.setDate(nextDate.getDate() + 1);
            const nextDefaultStart = nextDate.toISOString().split('T')[0];

            setTempPeriodeForm({
                libelle: '',
                ordre: periodesDec.length + (editingPeriodeId ? 1 : 2),
                date_debut: nextDefaultStart,
                date_fin: '',
                statut: 'OUVERTE'
            });

            fetchData();
        } catch (err) {
            showToast(err.response?.data?.error || "Erreur lors de l'enregistrement de la période", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleStartEditPeriode = (p) => {
        setEditingPeriodeId(p.id);
        setTempPeriodeForm({
            libelle: p.libelle,
            ordre: p.ordre || 1,
            date_debut: p.date_debut ? p.date_debut.split('T')[0] : '',
            date_fin: p.date_fin ? p.date_fin.split('T')[0] : '',
            statut: p.statut || 'OUVERTE'
        });
    };

    const handleCancelEditPeriode = (decoupage) => {
        setEditingPeriodeId(null);
        const periodesDec = periodesParDecoupage[decoupage.id] || [];
        let defaultStartDate = '';
        if (periodesDec.length > 0) {
            const lastPeriode = periodesDec[periodesDec.length - 1];
            if (lastPeriode.date_fin) {
                const nextDate = new Date(lastPeriode.date_fin);
                nextDate.setDate(nextDate.getDate() + 1);
                defaultStartDate = nextDate.toISOString().split('T')[0];
            }
        }
        setTempPeriodeForm({
            libelle: '',
            ordre: periodesDec.length + 1,
            date_debut: defaultStartDate,
            date_fin: '',
            statut: 'OUVERTE'
        });
    };

    // --- SOUMISSION DU DÉCOUPAGE ---
    const handleDecoupageSubmit = async (e) => {
        e.preventDefault();
        setActionLoading(true);
        try {
            const payload = { ...decoupageForm, annee_scolaire_id: decoupageForm.annee_scolaire_id || selectedAnneeId };
            if (editingId) {
                await API.put(`/scolarite/decoupages/${editingId}`, payload);
                showToast("Type de découpage mis à jour avec succès.");
            } else {
                await API.post('/scolarite/decoupages/creer', payload);
                showToast("Type de découpage créé avec succès.");
            }
            resetForms();
            fetchData();
        } catch (err) {
            showToast(err.response?.data?.error || "Une erreur est survenue", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleEditDecoupage = (dec) => {
        setEditingId(dec.id);
        setDecoupageForm({
            annee_scolaire_id: dec.annee_scolaire_id,
            libelle: dec.libelle,
            type_periodicite: dec.type_periodicite
        });
        setShowForm(true);
    };

    const askDelete = (id, type, label) => {
        setConfirmAction({
            show: true,
            id,
            type,
            message: `Confirmer la suppression de ${label} ? (Bloqué si des éléments dépendants y sont rattachés).`
        });
    };

    const processConfirm = async () => {
        const { id, type } = confirmAction;
        setConfirmAction({ show: false });
        setActionLoading(true);
        try {
            if (type === 'decoupage') {
                await API.delete(`/scolarite/decoupages/${id}`);
                showToast("Découpage supprimé avec succès.");
            } else {
                await API.delete(`/scolarite/periodes/${id}`);
                showToast("Période supprimée avec succès.");
            }
            fetchData();
        } catch (err) {
            showToast(err.response?.data?.error || "Suppression refusée", "error");
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div style={layoutStyle}>
            <Sidebar />
            <main style={mainStyle}>
                <header style={headerStyle}>
                    <div style={titleContainer}>
                        <div style={iconBoxStyle}><Calendar size={22} color="white" /></div>
                        <div>
                            <h1 style={titleStyle}>CALENDRIER & DÉCOUPAGE</h1>
                            <div style={subtitleStyle}><ShieldCheck size={14} /> Gestion des types de découpage et périodes scolaires</div>
                        </div>
                    </div>
                    <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                            <span style={{fontSize: '11px', fontWeight: '800', color: '#64748b'}}>Année :</span>
                            <select 
                                style={filterSelectStyle} 
                                value={selectedAnneeId} 
                                onChange={(e) => setSelectedAnneeId(e.target.value)}
                            >
                                <option value="">Toutes les années</option>
                                {annees.map(a => (
                                    <option key={a.id} value={a.id}>{a.libelle}</option>
                                ))}
                            </select>
                        </div>
                        <button 
                            onClick={() => {
                                if (showForm) resetForms();
                                else {
                                    resetForms();
                                    setShowForm(true);
                                }
                            }} 
                            style={showForm ? btnCancel : btnAdd}
                        >
                            {showForm ? <><X size={18} /> ANNULER</> : <><PlusCircle size={18} /> NOUVEAU DÉCOUPAGE</>}
                        </button>
                    </div>
                </header>

                {/* --- FORMULAIRE D'AJOUT / MODIFICATION DE DÉCOUPAGE --- */}
                {showForm && (
                    <div style={formWrapper}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
                            <span style={{fontSize: '11px', fontWeight: '800', color: '#2563eb', textTransform: 'uppercase'}}>
                                {editingId ? "Modification (Type de Découpage)" : "Création (Type de Découpage)"}
                            </span>
                            <button onClick={resetForms} style={{background: 'none', border: 'none', cursor: 'pointer', color: '#64748b'}}>
                                <X size={16} />
                            </button>
                        </div>
                        <form onSubmit={handleDecoupageSubmit} style={inlineForm}>
                            <div style={inputGroup}>
                                <label style={labelStyle}>ANNÉE SCOLAIRE</label>
                                <select 
                                    style={inputStyle} required
                                    value={decoupageForm.annee_scolaire_id} 
                                    onChange={(e) => setDecoupageForm({...decoupageForm, annee_scolaire_id: e.target.value})}
                                >
                                    <option value="">Sélectionner l'année...</option>
                                    {annees.map(a => (
                                        <option key={a.id} value={a.id}>{a.libelle}</option>
                                    ))}
                                </select>
                            </div>
                            <div style={inputGroup}>
                                <label style={labelStyle}>LIBELLÉ DU DÉCOUPAGE (Ex: Trimestres Lycée)</label>
                                <input 
                                    style={inputStyle} required placeholder="Ex: Trimestres Lycée"
                                    value={decoupageForm.libelle} 
                                    onChange={(e) => setDecoupageForm({...decoupageForm, libelle: e.target.value})} 
                                />
                            </div>
                            <div style={inputGroup}>
                                <label style={labelStyle}>NOMBRE DE PÉRIODES</label>
                                <input 
                                    type="number" min="1" style={inputStyle} required placeholder="Ex: 3"
                                    value={decoupageForm.type_periodicite} 
                                    onChange={(e) => setDecoupageForm({...decoupageForm, type_periodicite: e.target.value})} 
                                />
                            </div>
                            <button type="submit" disabled={actionLoading} style={btnSubmit}>
                                {editingId ? "METTRE À JOUR" : "ENREGISTRER"}
                            </button>
                        </form>
                    </div>
                )}

                {/* --- CONTENU DE LA LISTE DES DÉCOUPAGES --- */}
                <div style={tableWrapper}>
                    {loading ? (
                        <div style={centerStyle}><Layers className="animate-spin" size={40} color="#2563eb" /></div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {decoupages.map(dec => {
                                const ouvert = decoupageOuvertId === dec.id;
                                const periodesDec = periodesParDecoupage[dec.id] || [];

                                return (
                                    <div key={dec.id} style={classeCardStyle}>
                                        <div onClick={() => toggleAccordion(dec.id)} style={classeHeaderStyle}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', flex: 1 }}>
                                                <span style={chevronBox}>{ouvert ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</span>
                                                <span style={{ fontSize: '13px', fontWeight: '900', color: '#1e293b' }}>⚙️ {dec.libelle}</span>
                                                <span style={{background: '#eff6ff', color: '#2563eb', padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '800'}}>
                                                    Nombre de périodes : {dec.type_periodicite}
                                                </span>
                                                <span style={{background: '#f8fafc', color: '#64748b', padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '800', border: '1px solid #cbd5e1'}}>
                                                    {periodesDec.length} période(s) définie(s)
                                                </span>
                                            </div>
                                            <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
                                                <button onClick={(e) => { e.stopPropagation(); handleEditDecoupage(dec); }} style={btnEdit} title="Modifier le découpage"><Edit3 size={15} /></button>
                                                <button onClick={(e) => { e.stopPropagation(); askDelete(dec.id, 'decoupage', dec.libelle); }} style={btnDelete} title="Supprimer"><Trash2 size={15} /></button>
                                            </div>
                                        </div>

                                        {ouvert && (
                                            <div style={classeBodyStyle}>
                                                <div style={voletContainerStyle}>
                                                    <div style={voletTitleStyle}>
                                                        {editingPeriodeId ? "✏️ Modifier la Période" : "📅 Ajouter une Période"}
                                                    </div>
                                                    
                                                    {/* Formulaire direct de période dans l'accordéon */}
                                                    <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 0.5fr 1fr 1fr 1fr auto', gap: '10px', alignItems: 'end', marginBottom: '15px', background: '#f8fafc', padding: '10px', borderRadius: '8px' }}>
                                                        <div>
                                                            <label style={labelStyle}>Libellé Période</label>
                                                            <input 
                                                                style={inputStyle} placeholder="Ex: 1er Trimestre"
                                                                value={tempPeriodeForm.libelle}
                                                                onChange={(e) => setTempPeriodeForm({...tempPeriodeForm, libelle: e.target.value})}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label style={labelStyle}>Ordre</label>
                                                            <input 
                                                                type="number" min="1" style={inputStyle}
                                                                value={tempPeriodeForm.ordre}
                                                                onChange={(e) => setTempPeriodeForm({...tempPeriodeForm, ordre: e.target.value})}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label style={labelStyle}>Date Début</label>
                                                            <input 
                                                                type="date" style={inputStyle}
                                                                value={tempPeriodeForm.date_debut}
                                                                onChange={handleTempDateDebutChange}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label style={labelStyle}>Date Fin</label>
                                                            <input 
                                                                type="date" style={inputStyle}
                                                                min={getMinDateFin(tempPeriodeForm.date_debut)}
                                                                value={tempPeriodeForm.date_fin}
                                                                onChange={(e) => setTempPeriodeForm({...tempPeriodeForm, date_fin: e.target.value})}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label style={labelStyle}>Statut</label>
                                                            <select 
                                                                style={inputStyle}
                                                                value={tempPeriodeForm.statut}
                                                                onChange={(e) => setTempPeriodeForm({...tempPeriodeForm, statut: e.target.value})}
                                                            >
                                                                <option value="OUVERTE">Ouverte</option>
                                                                <option value="CLOTUREE">Clôturée</option>
                                                            </select>
                                                        </div>
                                                        <div style={{display: 'flex', gap: '5px'}}>
                                                            <button 
                                                                type="button" 
                                                                disabled={actionLoading}
                                                                onClick={() => handleSavePeriode(dec)}
                                                                style={btnPrimaryStyle}
                                                            >
                                                                {editingPeriodeId ? "Modifier" : "+ Ajouter"}
                                                            </button>
                                                            {editingPeriodeId && (
                                                                <button 
                                                                    type="button" 
                                                                    onClick={() => handleCancelEditPeriode(dec)}
                                                                    style={{background: '#cbd5e1', color: '#1e293b', border: 'none', borderRadius: '6px', padding: '10px', cursor: 'pointer', fontWeight: '800'}}
                                                                    title="Annuler"
                                                                >
                                                                    <X size={14}/>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Tableau des périodes configurées */}
                                                    <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                                            <thead>
                                                                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>
                                                                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Ordre</th>
                                                                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Libellé</th>
                                                                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Date Début</th>
                                                                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Date Fin</th>
                                                                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Statut</th>
                                                                    <th style={{ padding: '8px 12px', textAlign: 'center' }}>Actions</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {periodesDec.length === 0 ? (
                                                                    <tr>
                                                                        <td colSpan="6" style={{ textAlign: 'center', padding: '15px', color: '#94a3b8', fontStyle: 'italic' }}>
                                                                            Aucune période configurée pour ce découpage.
                                                                        </td>
                                                                    </tr>
                                                                ) : (
                                                                    periodesDec.map((p) => (
                                                                        <tr key={p.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                                            <td style={{ padding: '8px 12px' }}><code>{p.ordre}</code></td>
                                                                            <td style={{ padding: '8px 12px', fontWeight: 'bold' }}>{p.libelle}</td>
                                                                            <td style={{ padding: '8px 12px' }}>{p.date_debut?.split('T')[0]}</td>
                                                                            <td style={{ padding: '8px 12px' }}>{p.date_fin?.split('T')[0]}</td>
                                                                            <td style={{ padding: '8px 12px' }}>
                                                                                <span style={{ background: p.statut === 'OUVERTE' ? '#dcfce7' : '#fee2e2', color: p.statut === 'OUVERTE' ? '#16a34a' : '#dc2626', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold' }}>
                                                                                    {p.statut}
                                                                                </span>
                                                                            </td>
                                                                            <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                                                                <div style={{display: 'flex', gap: '6px', justifyContent: 'center'}}>
                                                                                    <button 
                                                                                        type="button" 
                                                                                        onClick={() => handleStartEditPeriode(p)}
                                                                                        style={btnEdit}
                                                                                        title="Modifier la période"
                                                                                    >
                                                                                        <Edit3 size={14}/>
                                                                                    </button>
                                                                                    <button 
                                                                                        type="button" 
                                                                                        onClick={() => askDelete(p.id, 'periode', p.libelle)}
                                                                                        style={btnDelete}
                                                                                        title="Supprimer la période"
                                                                                    >
                                                                                        <Trash2 size={14}/>
                                                                                    </button>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    ))
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {decoupages.length === 0 && <div style={emptyStyle}>Aucun type de découpage enregistré.</div>}
                        </div>
                    )}
                </div>

                {/* --- MODALE DE CONFIRMATION --- */}
                {confirmAction.show && (
                    <div style={confirmToastStyle}>
                        <div style={{display:'flex', alignItems:'center', gap:'12px', flex: 1}}>
                            <div style={warnIcon}><AlertTriangle size={20} color="white"/></div>
                            <span style={confirmText}>{confirmAction.message}</span>
                        </div>
                        <div style={{display:'flex', gap:'10px'}}>
                            <button onClick={()=>setConfirmAction({show:false})} style={btnToastCancel}>ANNULER</button>
                            <button onClick={processConfirm} style={btnToastConfirm}>CONFIRMER</button>
                        </div>
                    </div>
                )}

                {/* --- TOAST DE NOTIFICATION --- */}
                {toast.show && (
                    <div style={{...toastStyle, background: toast.type === 'success' ? '#0f172a' : '#dc2626'}}>
                        {toast.type === 'success' ? <CheckCircle2 size={18} color="#10b981" /> : <AlertTriangle size={18} color="white" />}
                        {toast.message}
                    </div>
                )}
            </main>
        </div>
    );
};

// --- STYLES CSS-IN-JS ---
const layoutStyle = { display: 'flex', height: '100vh', background: '#f8fafc', overflow: 'hidden' };
const mainStyle = { flex: 1, display: 'flex', flexDirection: 'column' };
const headerStyle = { background: 'white', padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' };
const titleContainer = { display: 'flex', alignItems: 'center', gap: '15px' };
const titleStyle = { margin: 0, fontSize: '18px', fontWeight: '900', color: '#0f172a' };
const subtitleStyle = { fontSize: '12px', color: '#64748b', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '5px' };
const iconBoxStyle = { background: '#2563eb', padding: '10px', borderRadius: '12px' };
const filterSelectStyle = { padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '12px', fontWeight: '700' };
const tableWrapper = { padding: '20px 40px', flex: 1, overflowY: 'auto' };
const emptyStyle = { textAlign: 'center', padding: '40px', color: '#64748b', fontWeight: '600' };
const btnEdit = { background: '#eff6ff', border: 'none', color: '#2563eb', padding: '8px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const btnDelete = { background: '#fef2f2', border: 'none', color: '#dc2626', padding: '8px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const formWrapper = { background: 'white', padding: '20px 40px', borderBottom: '1px solid #e2e8f0', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.01)' };
const inlineForm = { display: 'flex', alignItems: 'flex-end', gap: '20px', flexWrap: 'wrap' };
const inputGroup = { display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, minWidth: '180px' };
const labelStyle = { fontSize: '10px', fontWeight: '900', color: '#64748b' };
const inputStyle = { padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontWeight: '700', fontSize: '13px', width: '100%', boxSizing: 'border-box' };
const btnSubmit = { background: '#2563eb', color: 'white', border: 'none', padding: '11px 25px', borderRadius: '10px', fontWeight: '800', cursor: 'pointer', height: '41px' };
const btnAdd = { background: '#0f172a', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '10px', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' };
const btnCancel = { ...btnAdd, background: '#fee2e2', color: '#dc2626' };
const classeCardStyle = { background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', marginBottom: '12px' };
const classeHeaderStyle = { background: '#f8fafc', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' };
const chevronBox = { background: '#e2e8f0', padding: '4px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#334155' };
const classeBodyStyle = { padding: '20px', background: '#f8fafc' };
const voletContainerStyle = { background: 'white', padding: '15px', borderRadius: '10px', border: '1px solid #e2e8f0', marginBottom: '15px' };
const voletTitleStyle = { fontSize: '12px', fontWeight: '900', color: '#1e293b', marginBottom: '12px' };
const btnPrimaryStyle = { padding: '10px 15px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '800', cursor: 'pointer', fontSize: '11px', height: '41px' };
const confirmToastStyle = { position: 'fixed', bottom: '30px', left: '50%', transform: 'translateX(-50%)', background: 'white', padding: '15px 25px', borderRadius: '20px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', minWidth: '500px', zIndex: 9999, border: '2px solid #e2e8f0' };
const warnIcon = { background: '#f59e0b', padding: '8px', borderRadius: '12px' };
const confirmText = { fontWeight: '800', color: '#0f172a', fontSize: '13px' };
const btnToastConfirm = { background: '#0f172a', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '12px', fontWeight: '800', cursor: 'pointer' };
const btnToastCancel = { background: '#f1f5f9', color: '#64748b', border: 'none', padding: '10px 20px', borderRadius: '12px', fontWeight: '800', cursor: 'pointer' };
const toastStyle = { position: 'fixed', top: '20px', right: '20px', padding: '15px 30px', borderRadius: '12px', color: 'white', display: 'flex', alignItems: 'center', gap: '12px', zIndex: 9999, fontWeight: '700' };
const centerStyle = { display: 'flex', justifyContent: 'center', padding: '100px' };

export default DecoupagePeriode;